import { useMutation, useQuery } from "@apollo/client";
import { StarIcon } from "@heroicons/react/outline";
import { StarIcon as SolidStarIcon } from "@heroicons/react/solid";
import { useParams } from "react-router-dom";
import {
  GetArtifactSidebarQueryQuery,
  GetArtifactSidebarQueryQueryVariables,
} from "types/graphql-schema";

import addFavouriteArtifactMutation from "@apps/artifact-sidebar/graphql/add-favourite-artifact-mutation";
import getArtifactSidebarQuery from "@apps/artifact-sidebar/graphql/get-artifact-sidebar-query";
import removeFavouriteArtifactMutation from "@apps/artifact-sidebar/graphql/remove-favourite-artifact-mutation";
import ArtifactDropdownMenu from "@apps/artifact/artifact-dropdown-menu";
import getOverviewFavouriteGoalsQuery from "@apps/goal-overview/graphql/get-overview-favourite-goals-query";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import { maxKeyResults } from "@helpers/constants";
import { assertEdgesNonNull } from "@helpers/helpers";

import GoalContent from "./goal-content";

// Full-page view of a single goal. The goal body itself (title, description,
// details, key results, alignment, comments) is the shared GoalContent
// component, also rendered by the goal drawer.
const GoalPage = () => {
  const label = useLabel();
  const link = useLink();
  const currentUser = currentUserVar();
  const { artifactId: artifactIdParam } = useParams<{ artifactId: string }>();
  const artifactId = Number(artifactIdParam);

  const { loading, data } = useQuery<
    GetArtifactSidebarQueryQuery,
    GetArtifactSidebarQueryQueryVariables
  >(getArtifactSidebarQuery, {
    variables: { artifactId, keyResultLimit: maxKeyResults },
    errorPolicy: "ignore",
    skip: !artifactId || Number.isNaN(artifactId),
  });

  const artifact = data?.artifact;
  const goal =
    artifact && artifact.__typename === "GoalArtifactNode" ? artifact : null;

  useDocumentTitle(goal?.title || label("goal", { capitalize: true }));

  const favouriteIds = assertEdgesNonNull(currentUser.favouriteArtifacts).map(
    ({ id }) => id,
  );
  const isFavourite = !!goal && favouriteIds.includes(goal.id);
  const [addFavouriteArtifact, { loading: loadingAdd }] = useMutation(
    addFavouriteArtifactMutation,
  );
  const [removeFavouriteArtifact, { loading: loadingRemove }] = useMutation(
    removeFavouriteArtifactMutation,
  );
  const loadingToggleFavourite = loadingAdd || loadingRemove;

  const handleClickFavouriteButton = () => {
    if (!goal) return;
    const variables = { artifactId: goal.id };
    if (isFavourite) {
      removeFavouriteArtifact({
        variables,
        refetchQueries: [getOverviewFavouriteGoalsQuery],
      });
    } else {
      addFavouriteArtifact({
        variables,
        refetchQueries: [getOverviewFavouriteGoalsQuery],
      });
    }
  };

  // The goal was deleted from its dropdown menu — send the user back to the
  // overview since there is no longer a goal to show.
  const handleDeleteGoal = () => {
    link.redirect("/goals");
  };

  return (
    <Layout>
      <Layout.Header
        breadcrumbs={[
          {
            title: label("goal", { pluralize: true }),
            url: "/goals",
          },
          {
            title: goal?.title || label("goal", { capitalize: true }),
            url: `/goal/${artifactId}`,
          },
        ]}
      >
        {goal && (
          <div className="flex items-center gap-1">
            {loadingToggleFavourite && <Loading mini size="4" />}
            <Button
              square
              size={ButtonSize.medium}
              tooltip="Mark as favourite"
              theme={buttonTheme.iconGray}
              onClick={handleClickFavouriteButton}
              aria-label={`Favourite ${label("goal")} button`}
            >
              {isFavourite ? (
                <SolidStarIcon className="h-4 w-4 text-yellow-500" />
              ) : (
                <StarIcon className="h-4 w-4" />
              )}
            </Button>
            <ArtifactDropdownMenu
              size={ButtonSize.medium}
              artifact={goal}
              onDelete={handleDeleteGoal}
            />
          </div>
        )}
      </Layout.Header>
      <Layout.Container aria-label={label("goal", { capitalize: true })}>
        <Layout.Main width="md">
          {loading && !goal ? (
            <Loading className="py-8" />
          ) : !goal ? (
            <div className="text-gray-600">
              We cannot find this {label("goal")}.
            </div>
          ) : !goal.canRead.permission ? (
            <div className="text-gray-600">
              You do not have permission to view this {label("goal")}.
            </div>
          ) : (
            <GoalContent goal={goal} />
          )}
        </Layout.Main>
      </Layout.Container>
    </Layout>
  );
};

export default GoalPage;
